import {buildErrorCode} from '../errorCodes/utils/errorCode';
import {hel_net_is_logged_in} from '../logics/auth';
import {ETextParamType_OnStartScreenShare} from '../logics/const';
import {
  getSkywayAvatarNumberForSend,
  getSkywayCustomAvatarIconURL,
  getSkywayName,
  getSkywayStartToggle,
  getSkywayUseCustomAvatarIcon,
  setSkyRoomId,
  getMainAudioContext,
  getMicAccessableFlg,
  setMicAccessableFlg
} from '../logics/dataOps';
import {getVideo} from '../logics/domOps';
import {hel_net_replace_room_url_with_roomid} from '../logics/history';
import {
  hel_net_get_mic_device_list,
  hel_net_can_access_microphone,
  getLocalStream,
  setLocalStream,
  setLiveLocalStream
} from '../logics/localStream';
import {getMainCanvasDOMElement} from '../logics/mainCanvasDOM';
import {hel_setText, requestSendingReceivedPacketsFromSkyway} from '../logics/moduleCaller';
import {parseJwt} from '../logics/parseJwt';
import {
  hel_skyway_myinfo_bridge_call,
  setMicEnabled,
  setLiveMicEnabled,
  hel_send_notify_message
} from '../logics/shared';
import {
  hel_video_stop,
  hel_video_close_genericwindow,
  hel_skyway_play_remote_video_stream,
  helcore_video_play_mediastream,
  hel_video_open_genericwindow
} from '../logics/video_callee';
import {hel_show_fatal_error_dialog} from '../logics/widgetErrorDialog';

import {CChannelSessionApi} from './CChannelSessionApi';
import {CHeliLiveChannel} from './CHeliLiveChannel';
import {CHeliLiveChannelList} from './CHeliLiveChannelList';
import {CHeliPlayer} from './CHeliPlayer';
import {CHeliPosChannelList} from './CHeliPosChannelList';
import {CHeliPositionChannel} from './CHeliPositionChannel';
import {CHeliVoiceChannel} from './CHeliVoiceChannel';
import {CHeliVoiceChannelList} from './CHeliVoiceChannelList';
import {CAvatarInfo} from './apiNotificationLib/CAvatarInfo';
import {CHeliAgoraLiveChannelProvider} from './liveChannelProviders/CHeliAgoraLiveChannelProvider';
import {
  EHeliMicPermissionState,
  onChangedMicPermissionState,
  onNetLiveConfirmPlayVideo,
  onNetLiveDisconnected,
  onNetLiveVideoEnded,
  onNetLiveVideoPlay,
  onNetLiveVideoStarted,
  onNetLiveVideoStop
} from './networkCallback';
import {CHeliCarnelianPositionChannelProvider} from './positionChannelProviders/CHeliCarnelianPositionChannelProvider';
import {CHeliCarnelianPrismPositionChannelProvider} from './positionChannelProviders/CHeliCarnelianPrismPositionChannelProvider';
import {CHeliOldSkywayUnifiedPositionVoiceChannelProvider} from './positionChannelProviders/CHeliOldSkywayUnifiedPositionVoiceChannelProvider';
import {CHeliAgoraVoiceChannelProvider} from './voiceChannelProviders/CHeliAgoraVoiceChannelProvider';
import {CHeliSkywayVoiceChannelProvider} from './voiceChannelProviders/CHeliSkywayVoiceChannelProvider';
import {CHeliSmcnVoiceChannelProvider} from './voiceChannelProviders/CHeliSmcnVoiceChannelProvider';

import type {IHeliLiveChannelProvider} from './liveChannelProviders/IHeliLiveChannelProvider';
import type {IHeliPositionChannelProvider} from './positionChannelProviders/IHeliPositionChannelProvider';
import type {IHeliVoiceChannelProvider} from './voiceChannelProviders/IHeliVoiceChannelProvider';
import type {Command} from '../../../HeliportJS/dist/models/ingameStateManager';
import type {JsonObject} from '../../../HeliportJS/dist/models/json';
import type {Return} from '../../../HeliportJS/dist/models/return';
import type {
  ChannelSession,
  EnterPositionChannelData,
  EnterVoiceChannelData,
  LiveChannelDetail,
  PositionChannelDetail,
  RequestObjectOwnershipData,
  VoiceChannelDetail
} from '../types/network';
import type Peer from 'skyway-js';
import type {RoomStream} from 'skyway-js';

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var hel_onScreenShareStart: (() => void) | undefined;
  var hel_onReceiveNoticeScreenShareStart:
    | ((
        playerId: string,
        remoteVideoStream: MediaStream,
        videoItemName: string,
        videoMaterialName: string
      ) => void)
    | undefined;
  var hel_onReceiveNoticeScreenShareStop: ((playerId: string) => void) | undefined;
  /* eslint-enable */
}

export type IHeliMic = {
  /** @see https://developer.mozilla.org/ja/docs/Web/API/MediaDeviceInfo/label */
  label: string;
  /** @see https://developer.mozilla.org/ja/docs/Web/API/MediaDeviceInfo/deviceId */
  deviceId: string;
};

export interface IHeliPosChannelOptions {
  channelType: string | null;
  name: string | null;
  description: string | null;
}

export interface PositionChannelListOptions {
  offset: number | null;
  limit: number | null;
  excludeEmptyChannelFlg: boolean | null;
}

export interface IHeliVoiceChannelOptions {
  channelType: string | null;
  name: string | null;
  description: string | null;
  offset: number | null;
  limit: number | null;
}

export type IHeliRtcTokenRoomTarget = 'position_channel_code' | 'world_code' | 'live_channel_code';

export type Transform = {
  pos: {
    x: number;
    y: number;
    z: number;
  };
  rotate: {
    x: number;
    y: number;
    z: number;
    w: number;
  };
};

export interface ILibraryLoadable {
  ensureLibraryLoadedAndInitialized(): Promise<void>;
}

export interface IHeliMicResponse {
  status: string;
  device_info: IHeliMic | null;
  errorCode?: string;
}

const DEFAULT_VOICE_CHANNEL_NAME = 'Default';
export class CHeliNetwork {
  /**
   * @deprecated
   * {@link CHeliSkywayVoiceChannelProvider.prepareLeavePositionChannel}、
   * {@link CHeliAgoraVoiceChannelProvider.prepareLeavePositionChannel} から逆参照するためのpublic化。
   *
   * 将来的には {@link CHeliSmcnVoiceChannelProvider} に揃え、
   * すべての音声プロバイダでデフォルトボイスチャンネル + マルチボイスチャンネル構成とするため、
   * 上記メソッドからの参照が不要となる予定。
   * @see https://github.com/VRHIKKY/WorldViewer/issues/15126
   */
  micReady: boolean = false;
  /**
   * @deprecated
   * {@link CHeliSkywayVoiceChannelProvider.prepareLeavePositionChannel}、
   * {@link CHeliAgoraVoiceChannelProvider.prepareLeavePositionChannel} から逆参照するためのpublic化。
   *
   * 将来的には {@link CHeliSmcnVoiceChannelProvider} に揃え、
   * すべての音声プロバイダでデフォルトボイスチャンネル + マルチボイスチャンネル構成とするため、
   * 上記メソッドからの参照が不要となる予定。
   * @see https://github.com/VRHIKKY/WorldViewer/issues/15126
   */
  liveMicReady: boolean = false;
  // マイク許可状態
  #micPermissionState: EHeliMicPermissionState = EHeliMicPermissionState.Prompt;

  readonly #idPlayerPairs = new Map<string, CHeliPlayer>();

  // 画面共有
  #screenShare_CurrentVideoItemName: string = '';
  #screenShare_CurrentVideoMaterialName: string = '';

  // ライブチャンネル
  #liveVideoSrc: MediaStream | null = null;
  #liveVideoItem: {itemName: string; materialName: string} | null = null;
  #autoPlayLiveVideoStreamFlg: boolean = false;
  #stanbyPlayVideoStreamFlg: boolean = false;

  #skyway_reseved_remoteVideoStream: MediaStream | null = null;
  #skyway_reseved_videoItemName: string = '';
  #skyway_reseved_videoMaterialName: string = '';

  #selfPlayerInfo: ChannelSession | null = null;

  readonly #channelSessionApi: CChannelSessionApi;

  readonly #ownedObjectIdSetList: Set<string>;

  #positionChannelProvider: IHeliPositionChannelProvider | null = null;
  #voiceChannelProvider: IHeliVoiceChannelProvider | null = null;
  #liveChannelProvider: IHeliLiveChannelProvider | null = null;

  #currentPositionChannel: CHeliPositionChannel | null = null;
  #currentVoiceChannel: CHeliVoiceChannel | null = null;
  #currentLiveChannel: CHeliLiveChannel | null = null;

  #currentMic: IHeliMic | null = null;

  #errorCode: string | null = null;
  #errorData: unknown = null;

  #defaultDummyVoiceChannel: CHeliVoiceChannel | null = null;

  // Audio Playback Retry Fields for iOS Safari Policy
  readonly #pendingRemoteAudioList = new Map<number, HTMLAudioElement>();
  #remoteAudioRetryInProcessFlg: boolean = false;
  #remoteAudioRetryInitialized: boolean = false;
  #remoteAudioRetryStartedFlg: boolean = false;

  constructor() {
    this.#channelSessionApi = new CChannelSessionApi();
    this.#ownedObjectIdSetList = new Set();
    this.#initEvent();
  }

  /** RTC種別のセット */
  async setRtcProvider(channelid: string): Promise<void> {
    const posChannel = await this.getPosChannel(channelid);
    const posChannelDetail = posChannel.getPositionChannelDetail();
    if (!posChannelDetail) {
      throw new Error(`posChannel is null : ${channelid}`);
    }

    const positionRtcType = posChannelDetail.position_rtc_type;
    const voiceRtcType = posChannelDetail.voice_rtc_type;
    const isPrism = posChannelDetail.is_prism;

    if (positionRtcType === 'skyway') {
      const unifiedProvider = new CHeliOldSkywayUnifiedPositionVoiceChannelProvider();
      this.#positionChannelProvider = unifiedProvider;
      this.#voiceChannelProvider = unifiedProvider;
      this.#liveChannelProvider = null;
    } else {
      // 別API上でもisPrismを参照しているため、条件に含める
      if (isPrism && positionRtcType === 'carnelian-prism') {
        this.#positionChannelProvider = new CHeliCarnelianPrismPositionChannelProvider();
      } else {
        this.#positionChannelProvider = new CHeliCarnelianPositionChannelProvider();
      }

      this.#liveChannelProvider = new CHeliAgoraLiveChannelProvider();

      switch (voiceRtcType) {
        case 'agora':
          this.#voiceChannelProvider = new CHeliAgoraVoiceChannelProvider();
          break;

        case 'skyway2':
          this.#voiceChannelProvider = new CHeliSkywayVoiceChannelProvider();
          break;

        case 'smcn':
          this.#voiceChannelProvider = new CHeliSmcnVoiceChannelProvider();
          break;

        default:
          throw new Error('RtcVoice type error : ' + voiceRtcType);
      }
    }
    console.debug(
      `setRtcProvider PositionRTC:${positionRtcType} VoiceRTC:${voiceRtcType} isPrism:${isPrism}`
    );

    try {
      await this.ensureLibraryLoadedAndInitialized();
    } catch (e) {
      hel_show_fatal_error_dialog('HEL02000000', String(e));
      throw e;
    }

    const voiceChannelProvider = this.#voiceChannelProvider;
    this.#positionChannelProvider.addEventListener('afterjoinpositionrtc', (event): void => {
      if (!this.#isUnifiedChannelProvider()) {
        // 座標Chが非MultiVCの場合、音声RTCに自動接続する
        if (!this.#currentPositionChannel?.isMultiVc()) {
          this.getMicDevice().then((pIHeliMic) => {
            voiceChannelProvider.joinVoiceRtc(pIHeliMic, 'position_channel_code');
          });
        }
      }
      this.#startOwnershipHeartbeatInterval();
    });
    if (this.#liveChannelProvider) {
      this.#liveChannelProvider.addEventListener('videostreamchanged', (event) => {
        const isPlayingLiveVideo = this.isPlayingLiveVideo();
        if (!event.detail) {
          this.#stopRemoteVideoLiveStream();
          return;
        }
        if (isPlayingLiveVideo) {
          this.#setLiveVideoSrc(event.detail);
          this.playLiveVideo();
        } else {
          this.#prepareLiveVideoSrc(event.detail);
        }
      });
    }
  }

  /** マイクデバイスの取得 */
  async getMicDevice(): Promise<IHeliMic | null> {
    // 既にマイクデバイスIDが取得済みの場合、別ボイスチャンネルに入っても設定を引き継ぐ
    if (this.#currentMic) {
      return this.#currentMic;
    }
    return await this.#getDefaultMicDevice();
  }

  /** ブラウザが認識しているマイクデバイスの取得 */
  async #getDefaultMicDevice(): Promise<IHeliMic | null> {
    const devices = await hel_net_get_mic_device_list();
    const device = devices?.[0];
    this.#currentMic = device
      ? {
          label: device.label,
          deviceId: device.deviceId
        }
      : null;
    return this.#currentMic;
  }

  #initEvent() {
    window.addEventListener('pagehide', (event: PageTransitionEvent) => {
      // APIへ退出通知
      globalThis.heliport.v3.api.channelSessionApi.sendLeaveBeacon();
    });
  }

  getErrorCode(): string | null {
    return this.#errorCode;
  }
  getErrorData(): unknown {
    return this.#errorData;
  }
  setError({code, data}: {code: string | null; data: unknown}) {
    this.#errorCode = code;
    this.#errorData = data;
  }
  isValid() {
    return this.#errorCode === null && this.#errorData === null;
  }

  async ensureLibraryLoadedAndInitialized(): Promise<void> {
    if (!this.#positionChannelProvider || !this.#voiceChannelProvider) {
      throw new Error('this.#positionChannelProvider or this.#voiceChannelProvider is null.');
    }
    await Promise.all([
      this.#positionChannelProvider.ensureLibraryLoadedAndInitialized(),
      this.#voiceChannelProvider.ensureLibraryLoadedAndInitialized(),
      this.#liveChannelProvider?.ensureLibraryLoadedAndInitialized()
    ]);
  }

  isConnectedPositionChannel(): boolean {
    if (!this.#positionChannelProvider) {
      return false;
    }

    return this.#positionChannelProvider.isConnected() &&
      this.#positionChannelProvider.isConnectedPositionChannel() &&
      this.#currentPositionChannel &&
      this.#positionChannelProvider.isConnectedToPositionChannel(this.#currentPositionChannel)
      ? true
      : false;
  }

  isConnectedVoiceChannel(): boolean {
    return this.#voiceChannelProvider?.isConnected() ?? false;
  }

  // 接続

  /** @returns 旧Skywayの場合は `Peer | null`。それ以外の場合は `null`。 */
  async connect(configKey?: string): Promise<Peer | null> {
    // このタイミングでマイクデバイスの初期化が行われていない（localMediaStreamが空）場合、改めて初期化を行う
    const localStream = getLocalStream();
    if (!localStream) {
      await this.initMicDevice();
    }

    const positionChannelProvider = this.#positionChannelProvider;
    if (!positionChannelProvider) {
      return null;
    }

    const voiceChannelProvider = this.#voiceChannelProvider;
    if (!voiceChannelProvider) {
      return null;
    }

    if (!voiceChannelProvider.prepareForNewJoin()) {
      return null;
    }

    const peerOrBoolean = await positionChannelProvider.connect(configKey);
    if (!peerOrBoolean) {
      return null;
    }

    positionChannelProvider.addEventListener('data', (event) => {
      voiceChannelProvider.onData(event.detail.data, event.detail.playerId);
    });

    positionChannelProvider.addEventListener('remotejoin', (event) => {
      window.setTimeout(() => {
        // NOTE: joinコマンドをC++層へ送った直後にmyavatarコマンドを送るとアバターが読み込まれないため、遅延を入れている
        // https://discord.com/channels/596169759045976094/1311264772942991421/1311511060393623572
        CAvatarInfo.setAvatarInfoForExistingUsers(event.detail.playerId);
      }, 500);

      voiceChannelProvider.onRemoteJoin();
    });

    positionChannelProvider.addEventListener('remoteleave', (event) => {
      requestSendingReceivedPacketsFromSkyway('leave', event.detail.playerId, '');
      voiceChannelProvider.onRemoteLeave(event.detail);
      this.releasePlayer(event.detail.playerId);
    });

    voiceChannelProvider.connectVoiceChannel();

    if (this.#liveChannelProvider) {
      this.#liveChannelProvider.initLiveClient();
    }

    this.#channelSessionApi.startHeartbeat();

    return peerOrBoolean === true ? null : peerOrBoolean;
  }

  async disconnect(): Promise<void> {
    if (!this.#currentPositionChannel) {
      return;
    }
    await this.leave();
    this.#voiceChannelProvider?.disconnectFromVoiceChannel();
    this.#channelSessionApi.stopHeartbeat();
    this.micReady = false;
    this.liveMicReady = false;
  }

  async leave(): Promise<void> {
    if (this.#currentLiveChannel) {
      await this.leaveLiveChannel(this.#currentLiveChannel);
    }

    if (this.#currentVoiceChannel) {
      await this.leaveVoiceChannel();
    }

    if (this.#defaultDummyVoiceChannel) {
      this.#defaultDummyVoiceChannel = null;
    }

    if (this.#currentPositionChannel) {
      await this.#voiceChannelProvider?.prepareLeavePositionChannel();

      await this.#positionChannelProvider?.leavePositionChannel();
      try {
        const resLeavePosCh =
          await globalThis.heliport.v3.api.channelSessionApi.leavePositionChannel();
        if (resLeavePosCh.status !== 'ok') {
          this.setError({
            code: 'errorCode' in resLeavePosCh ? resLeavePosCh.errorCode : resLeavePosCh.code,
            data: resLeavePosCh.data
          });
        }
      } catch (error) {
        this.setError({code: '400', data: error});
      }
      CAvatarInfo.clearChannelSessionCodeAvatarInfoPairs();

      this.#currentPositionChannel = null;
    }
  }

  /**
   * @param positionChannel
   * @param authorityJwt
   * @param ticketJwt
   * @returns 入室に成功したら `true` を返す。
   */
  async joinPositionChannel(
    positionChannel: CHeliPositionChannel,
    authorityJwt: string | null,
    ticketJwt: string | null
  ): Promise<boolean> {
    const channelId = positionChannel.getChannelId();
    console.debug('joinPosChannel start channelid:', channelId);
    if (!channelId) {
      console.error(
        'iHeliNet joinPositionChannel error: positionChannel.getChannelId() returns null'
      );
      positionChannel.setError({code: '400', data: 'positionChannel.getChannelId() returns null'});
      return false;
    }
    const authorizationJwt = authorityJwt;
    // API対応したらHeliportも対応して入室リクエストに使用する
    const resEntryPch = (await globalThis.heliport.v3.api.channelSessionApi.enterPositionChannel(
      channelId,
      authorizationJwt ?? undefined,
      ticketJwt ?? undefined
    )) as Return<EnterPositionChannelData>;
    if (resEntryPch.status !== 'ok') {
      // 入室エラー
      positionChannel.setError({
        code: 'errorCode' in resEntryPch ? resEntryPch.errorCode : resEntryPch.code,
        data: resEntryPch.data
      });
      // NOTE: これがなければ、満室時に再入室を行うとき、同一PeerIdのエラーが出る
      await this.#positionChannelProvider?.leavePositionChannel();
      return false;
    }
    console.debug('joinPosChannel ChS-API enterPos resEntryPch:', resEntryPch);

    const channelSessionJwt = (
      await globalThis.heliport.v3.api.channelSessionApi.getChannelSessionJwt()
    ).data;
    if (!channelSessionJwt) {
      this.setError({code: '400', data: 'channelSessionJwt is null'});
      return false;
    }

    await this.applyPositionChannelSession(resEntryPch.data.position_channel, channelSessionJwt);
    return true;
  }

  /**
   * Channel Session APIサーバーの座標チャンネル入室状態を反映する前に必要な処理を行う。
   * @remarks
   * Canvas層の次のコードと同じ処理。
   * https://github.com/VRHIKKY/WorldViewer/blob/702f936960bbdc5b4346414452ff99e1a0ecd769/Heliodor/ReleasePage/ui2024/data/Canvas/HeliScript/VoiceChatChannel/ChSModelMultiple.hs#L597-L608
   * @param positionChannelId
   * @returns
   */
  async initializeChannelSessionContext(positionChannelId: string): Promise<void> {
    setSkyRoomId(positionChannelId);
    hel_net_replace_room_url_with_roomid(positionChannelId);
    await this.setRtcProvider(positionChannelId);
    await this.connect();
  }

  /**
   * Channel Session APIサーバーの座標チャンネル入室状態を反映します。
   * @param positionChannelDetail
   * @param channelSessionJwt
   */
  async applyPositionChannelSession(
    positionChannelDetail: PositionChannelDetail,
    channelSessionJwt: string
  ): Promise<void> {
    if (!this.#positionChannelProvider || !this.#voiceChannelProvider) {
      throw new Error('this.#positionChannelProvider or this.#voiceChannelProvider is null.');
    }

    try {
      this.#selfPlayerInfo = parseJwt(channelSessionJwt) as ChannelSession;
      this.#currentPositionChannel = new CHeliPositionChannel(positionChannelDetail);

      await this.#positionChannelProvider.joinPositionChannel(channelSessionJwt);

      if (
        this.#currentPositionChannel.isMultiVc() &&
        this.#currentPositionChannel.isEnableDefaultVc()
      ) {
        // デフォルトグループ用ダミーボイスチャンネルの作成
        this.#defaultDummyVoiceChannel = this.#generateDefaultDummyVoiceChannel(
          positionChannelDetail,
          this.#selfPlayerInfo.user_code
        );
        this.#currentVoiceChannel = this.#defaultDummyVoiceChannel;
      }

      await this.#voiceChannelProvider.onJoinPositionChannel(
        positionChannelDetail,
        channelSessionJwt
      );
    } catch (error) {
      console.error('CHeliNetwork.applyPositionChannelSession() failed:', error);
      this.#currentPositionChannel?.setError({code: '400', data: error});
    }
  }

  getPlayerId(): string | null {
    return !this.#positionChannelProvider ? null : this.#positionChannelProvider.getPlayerId();
  }

  // マイク許可状態

  getMicPermissionState(): EHeliMicPermissionState {
    return this.#micPermissionState;
  }

  async checkMicPermissionState(): Promise<void> {
    const prevPermissionState = this.#micPermissionState;

    try {
      // ※暫定不具合回避実装（Android版ミニアプリ対応）
      // 既にマイクアクセス許可判定されているなら、フラグを参照する
      const permissionState = getMicAccessableFlg()
        ? 'granted'
        : (
            await window.navigator.permissions.query({
              name: 'microphone'
            })
          ).state;

      if (permissionState === 'denied') {
        this.#micPermissionState = EHeliMicPermissionState.Denied;
      } else if (permissionState === 'granted') {
        this.#micPermissionState = EHeliMicPermissionState.Granted;
        setMicAccessableFlg(true);
      } else if (permissionState === 'prompt') {
        this.#micPermissionState = EHeliMicPermissionState.Prompt;
      } else {
        this.#micPermissionState = EHeliMicPermissionState.Prompt;
      }
      // iOSではマイク不許可時にDeniedではなくPromptしか返ってこず、マイク許可を促すCanvasが出なくなってしまうので、MediaStreamの生成是非で判定する
      if (permissionState === 'prompt') {
        const fallbackCheck = await hel_net_can_access_microphone();
        if (fallbackCheck) {
          this.#micPermissionState = EHeliMicPermissionState.Granted;
        } else {
          this.#micPermissionState = EHeliMicPermissionState.Denied;
        }
      }
    } catch (e) {
      // Firefox ESR 128 (2025-09-16に同バージョンはサポート終了) では'microphone'に対応していないので、LocalStreamの設定状態で判別する
      if (this.#voiceChannelProvider?.useNotEmptyLocalStream()) {
        this.#micPermissionState = EHeliMicPermissionState.Granted;
      } else {
        this.#micPermissionState = EHeliMicPermissionState.Denied;
      }
    }

    // 状態変更によるコールバック呼び出し
    if (prevPermissionState !== this.#micPermissionState) {
      onChangedMicPermissionState(this.#micPermissionState);
    }
  }

  // 座標チャンネル

  async createPosChannel(
    spatiumCode: string,
    worldCode: string,
    params: IHeliPosChannelOptions
  ): Promise<CHeliPositionChannel> {
    try {
      const resCreatePosChannel =
        await globalThis.heliport.v3.api.channelSessionApi.createPositionChannel(
          spatiumCode,
          worldCode,
          params.channelType,
          params.name,
          params.description
        );
      if (resCreatePosChannel.status !== 'ok') {
        const iHeliPositionChannel = new CHeliPositionChannel(null);
        iHeliPositionChannel.setError({
          code:
            'errorCode' in resCreatePosChannel
              ? resCreatePosChannel.errorCode
              : resCreatePosChannel.code,
          data: resCreatePosChannel.data
        });
        return iHeliPositionChannel;
      }
      const postionChannelDetail = resCreatePosChannel.data
        .position_channel as PositionChannelDetail;
      const iHeliPositionChannel = new CHeliPositionChannel(postionChannelDetail);
      return iHeliPositionChannel;
    } catch (error) {
      const iHeliPositionChannel = new CHeliPositionChannel(null);
      iHeliPositionChannel.setError({code: '400', data: error});
      return iHeliPositionChannel;
    }
  }

  // 座標チャンネル

  async getPosChannel(channelid: string): Promise<CHeliPositionChannel> {
    try {
      const resPosChDetail =
        await globalThis.heliport.v3.api.channelSessionApi.fetchPositionChannelDetail(channelid);
      if (resPosChDetail.status !== 'ok') {
        const iHeliPositionChannel = new CHeliPositionChannel(null);
        iHeliPositionChannel.setError({
          code: 'errorCode' in resPosChDetail ? resPosChDetail.errorCode : resPosChDetail.code,
          data: resPosChDetail.data
        });
        return iHeliPositionChannel;
      }
      const postionChannelDetail = resPosChDetail.data.position_channel as PositionChannelDetail;
      return new CHeliPositionChannel(postionChannelDetail);
    } catch (error) {
      const iHeliPositionChannel = new CHeliPositionChannel(null);
      iHeliPositionChannel.setError({code: '400', data: error});
      return iHeliPositionChannel;
    }
  }

  async getPosChannelList(
    spatiumcode: string,
    worldCode: string,
    params: PositionChannelListOptions
  ): Promise<CHeliPosChannelList> {
    try {
      const resPosChList =
        await globalThis.heliport.v3.api.channelSessionApi.fetchWorldPositionChannelList(
          spatiumcode,
          worldCode,
          params.excludeEmptyChannelFlg ?? true
        );
      if (resPosChList.status !== 'ok') {
        const iHeliPosChList = new CHeliPosChannelList();
        iHeliPosChList.setError({
          code: 'errorCode' in resPosChList ? resPosChList.errorCode : resPosChList.code,
          data: resPosChList.data
        });
        return iHeliPosChList;
      }
      const posChList = (resPosChList.data.position_channels as PositionChannelDetail[]).map(
        (postionChannel) => {
          return new CHeliPositionChannel(postionChannel);
        }
      );

      return new CHeliPosChannelList(posChList, params.offset ?? 0, params.limit ?? null);
    } catch (error) {
      const iHeliPosChList = new CHeliPosChannelList();
      iHeliPosChList.setError({code: '400', data: error});
      return iHeliPosChList;
    }
  }

  getCurrentPositionChannel(): CHeliPositionChannel | null {
    return this.#currentPositionChannel;
  }

  // 音声チャンネル

  /**
   * デフォルトグループ用ダミーボイスチャンネルの作成
   * @param positionChannelDetail
   * @param userCode
   * @returns
   */
  #generateDefaultDummyVoiceChannel(
    positionChannelDetail: PositionChannelDetail,
    userCode: string
  ): CHeliVoiceChannel {
    const voiceChannelDetail: VoiceChannelDetail = {
      channel_id: positionChannelDetail.channel_id + '__' + DEFAULT_VOICE_CHANNEL_NAME,
      position_channel_id: positionChannelDetail.id?.toString() || '0',
      channel_type: positionChannelDetail.channel_type,
      max_player_count: positionChannelDetail.max_player_count,
      name: DEFAULT_VOICE_CHANNEL_NAME,
      create_user_code: userCode,
      description: '',
      users: [],
      channel_sessions: [],
      is_global: true,
      rtc_config: positionChannelDetail.rtc_config
    };

    return new CHeliVoiceChannel(voiceChannelDetail);
  }

  /**
   * 単一プロトコルによって座標チャンネルと音声チャンネルが1:1対応で提供されている場合に `true` を返す。
   * @remarks
   * 現在は旧Skyway ({@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider}) の場合。
   * @returns
   */
  #isUnifiedChannelProvider(): boolean {
    return (this.#positionChannelProvider as unknown) === this.#voiceChannelProvider;
  }

  async createVoiceChannel(
    posionChannelCode: string,
    params: IHeliVoiceChannelOptions
  ): Promise<CHeliVoiceChannel | null> {
    if (this.#isUnifiedChannelProvider()) {
      return null;
    }
    try {
      const resCreateVoChannel =
        await globalThis.heliport.v3.api.channelSessionApi.createVoiceChannel(
          posionChannelCode,
          params.channelType,
          params.name,
          params.description,
          null
        );
      if (resCreateVoChannel.status !== 'ok') {
        const iHeliVoiceChannel = new CHeliVoiceChannel(null);
        iHeliVoiceChannel.setError({
          code:
            'errorCode' in resCreateVoChannel
              ? resCreateVoChannel.errorCode
              : resCreateVoChannel.code,
          data: resCreateVoChannel.data
        });
        return iHeliVoiceChannel;
      }
      const voiceChannelDetail = resCreateVoChannel.data.voice_channel as VoiceChannelDetail;
      return new CHeliVoiceChannel(voiceChannelDetail);
    } catch (error) {
      const iHeliVoiceChannel = new CHeliVoiceChannel(null);
      iHeliVoiceChannel.setError({code: '400', data: error});
      return iHeliVoiceChannel;
    }
  }

  async getVoiceChannel(channelid: string): Promise<CHeliVoiceChannel | null> {
    if (this.#isUnifiedChannelProvider()) {
      return null;
    }
    try {
      const resVoChDetail =
        await globalThis.heliport.v3.api.channelSessionApi.fetchVoiceChannelDetail(channelid);
      if (resVoChDetail.status !== 'ok') {
        const iHeliVoitionChannel = new CHeliVoiceChannel(null);
        iHeliVoitionChannel.setError({
          code: 'errorCode' in resVoChDetail ? resVoChDetail.errorCode : resVoChDetail.code,
          data: resVoChDetail.data
        });
        return iHeliVoitionChannel;
      }
      const voiceChannelDetail = resVoChDetail.data.voice_channel as VoiceChannelDetail;
      return new CHeliVoiceChannel(voiceChannelDetail);
    } catch (error) {
      const iHeliVoiceChannel = new CHeliVoiceChannel(null);
      iHeliVoiceChannel.setError({code: '400', data: error});
      return iHeliVoiceChannel;
    }
  }

  async getVoiceChannelList(
    poschannelid: string,
    params: IHeliVoiceChannelOptions
  ): Promise<CHeliVoiceChannelList | null> {
    if (this.#isUnifiedChannelProvider()) {
      return null;
    }
    try {
      const resVoChList =
        await globalThis.heliport.v3.api.channelSessionApi.fetchPositionChannelVoiceChannelList(
          poschannelid
        );
      if (resVoChList.status !== 'ok') {
        const iHeliVoiceChList = new CHeliVoiceChannelList();
        iHeliVoiceChList.setError({
          code: 'errorCode' in resVoChList ? resVoChList.errorCode : resVoChList.code,
          data: resVoChList.data
        });
        return iHeliVoiceChList;
      }
      const voChList = (resVoChList.data.voice_channels as VoiceChannelDetail[]).map(
        (voiceChannel) => new CHeliVoiceChannel(voiceChannel)
      );

      const offset = params.offset ? params.offset : 0;
      const limit = params.limit ? params.limit : null;

      return new CHeliVoiceChannelList(voChList, offset, limit);
    } catch (error) {
      const iHeliVoiceChList = new CHeliVoiceChannelList();
      iHeliVoiceChList.setError({code: '400', data: error});
      return iHeliVoiceChList;
    }
  }

  /**
   * @deprecated
   * {@link CHeliSmcnVoiceChannelProvider} から逆参照を行うためのメソッド。
   */
  setCurrentVoiceChannel(voiceChannel: CHeliVoiceChannel | null): void {
    this.#currentVoiceChannel = voiceChannel;
  }
  getCurrentVoiceChannel(): CHeliVoiceChannel | null {
    return this.#currentVoiceChannel;
  }

  isEnableJoinVoiceChannel(): boolean {
    // 座標チャンネルに接続済みか判定
    if (
      !this.#currentPositionChannel ||
      !this.#positionChannelProvider?.isConnectedToPositionChannel(this.#currentPositionChannel)
    ) {
      console.error('Could not connect VoCh. connect PosCh first.');
      return false;
    }

    if (this.#isUnifiedChannelProvider()) {
      return false;
    }
    return true;
  }

  async joinDefaultVoiceChannel(): Promise<void> {
    if (!this.isEnableJoinVoiceChannel()) {
      return;
    }

    // デフォルトボイスチャンネルを使うか否か
    if (!this.#currentPositionChannel?.isEnableDefaultVc()) {
      return;
    }

    console.debug('joinDefaultVoiceChannel start:');

    // デフォルトグループにする
    this.#currentVoiceChannel = this.#defaultDummyVoiceChannel;
    try {
      const pIHeliMic = await this.getMicDevice();
      await this.#voiceChannelProvider?.joinVoiceRtc(pIHeliMic, 'position_channel_code');
      console.debug('joinDefaultVoiceChannel done');
    } catch (error) {
      this.setError({code: '400', data: error});
      return;
    }
  }

  async joinVoiceChannel(
    voiceChannel: CHeliVoiceChannel,
    pHeliMic: IHeliMic | null
  ): Promise<void> {
    if (!this.isEnableJoinVoiceChannel()) {
      return;
    }

    console.debug('joinVoiceChannel start:', pHeliMic);

    // 音声チャンネルが接続中の座標チャンネルの親か判定
    if (!this.#currentPositionChannel) {
      return;
    }
    const positionChannelId = this.#currentPositionChannel.getChannelId();
    const voiceChannelId = voiceChannel.getChannelId();
    if (!positionChannelId || !voiceChannelId || !voiceChannelId.startsWith(positionChannelId)) {
      console.error('Could not connect VoCh. connect diffrent parent PosCh.');
      return;
    }
    try {
      const resEntryVoCh = (await globalThis.heliport.v3.api.channelSessionApi.enterVoiceChannel(
        voiceChannelId
      )) as Return<EnterVoiceChannelData>;
      if (resEntryVoCh.status !== 'ok') {
        // 入室エラー
        voiceChannel.setError({
          code: 'errorCode' in resEntryVoCh ? resEntryVoCh.errorCode : resEntryVoCh.code,
          data: resEntryVoCh.data
        });
        return;
      }
      this.#currentVoiceChannel = new CHeliVoiceChannel(resEntryVoCh.data.voice_channel);

      await this.#voiceChannelProvider?.joinVoiceRtc(
        pHeliMic,
        this.#currentPositionChannel.isMultiVc() ? null : 'position_channel_code'
      );
    } catch (error) {
      if (this.#currentVoiceChannel?.getChannelId() === voiceChannelId) {
        await this.leaveVoiceChannel();
      }
      voiceChannel.setError({code: '400', data: error});
      return;
    }
  }

  #isCurrentVoiceChannelDefault(): boolean {
    if (!this.#currentPositionChannel?.isEnableDefaultVc()) {
      return false;
    }
    const currentVoiceChannel = this.#currentVoiceChannel;
    if (!currentVoiceChannel) {
      return false;
    }
    if (currentVoiceChannel.getValue('name') === DEFAULT_VOICE_CHANNEL_NAME) {
      return true;
    }
    return false;
  }

  async leaveVoiceChannel(): Promise<void> {
    if (this.#isUnifiedChannelProvider()) {
      return;
    }

    // デフォルトグループの場合、API上のvoiceChannelは無いので、APIアクセスはしない
    if (!this.#isCurrentVoiceChannelDefault()) {
      try {
        const resLeaveVosCh =
          await globalThis.heliport.v3.api.channelSessionApi.leaveVoiceChannel();
        if (resLeaveVosCh.status !== 'ok') {
          this.#currentVoiceChannel?.setError({
            code: 'errorCode' in resLeaveVosCh ? resLeaveVosCh.errorCode : resLeaveVosCh.code,
            data: resLeaveVosCh.data
          });
          return;
        }
      } catch (error) {
        this.#currentVoiceChannel?.setError({code: '400', data: error});
        return;
      }
    }
    this.#currentVoiceChannel = null;

    this.#voiceChannelProvider?.postLeaveVoiceChannelCleanup();
  }

  /** 音声チャンネルで使用するTransform情報 */
  setCurrentLocalTransform(param: {player: Transform; camera: Transform}) {
    this.#voiceChannelProvider?.setCurrentLocalTransform(param);
  }

  // ライブチャンネル

  async getLiveChannel(channelid: string): Promise<CHeliLiveChannel | null> {
    if (!this.#liveChannelProvider) {
      return null;
    }
    try {
      const resLiveChDetail =
        await globalThis.heliport.v3.api.channelSessionApi.fetchLiveChannelDetail(channelid);
      if (resLiveChDetail.status !== 'ok') {
        const iHeliVoitionChannel = new CHeliLiveChannel(null);
        iHeliVoitionChannel.setError({
          code: 'errorCode' in resLiveChDetail ? resLiveChDetail.errorCode : resLiveChDetail.code,
          data: resLiveChDetail.data
        });
        return iHeliVoitionChannel;
      }
      const liveChannelDetail = resLiveChDetail.data.live_channel as LiveChannelDetail;
      return new CHeliLiveChannel(liveChannelDetail);
    } catch (error) {
      const liveChannel = new CHeliLiveChannel(null);
      liveChannel.setError({code: '400', data: error});
      return liveChannel;
    }
  }

  /** 座標チャンネル単位でのライブチャンネル一覧取得 */
  async getPositionChannelLiveChannelList(
    posionChannelCode: string
  ): Promise<CHeliLiveChannelList> {
    if (!this.#liveChannelProvider) {
      throw new Error('Method not implemented.');
    }
    const result =
      await globalThis.heliport.v3.api.channelSessionApi.fetchPositionChannelLiveChannelList(
        posionChannelCode
      );
    if (result.status !== 'ok') {
      const liveChannelList = new CHeliLiveChannelList();
      liveChannelList.setError({
        code: 'errorCode' in result ? result.errorCode : result.code,
        data: result.data
      });
      return liveChannelList;
    }
    const liveChannels = (result.data.live_channels as LiveChannelDetail[]).map((liveChannel) => {
      return new CHeliLiveChannel(liveChannel);
    });
    return new CHeliLiveChannelList(liveChannels);
  }

  /** ワールド単位でのライブチャンネル一覧取得 */
  async getWorldLiveChannelList(
    spatiumCode: string,
    worldCode: string
  ): Promise<CHeliLiveChannelList | null> {
    if (!this.#liveChannelProvider) {
      return null;
    }
    const result = await globalThis.heliport.v3.api.channelSessionApi.fetchWorldLiveChannelList(
      spatiumCode,
      worldCode
    );
    if (result.status !== 'ok') {
      const liveChannelList = new CHeliLiveChannelList();
      liveChannelList.setError({
        code: 'errorCode' in result ? result.errorCode : result.code,
        data: result.data
      });
      return liveChannelList;
    }
    const liveChannels = (result.data.live_channels as LiveChannelDetail[]).map(
      (liveChannel) => new CHeliLiveChannel(liveChannel)
    );
    return new CHeliLiveChannelList(liveChannels);
  }

  getCurrentLiveChannel(): CHeliLiveChannel | null {
    return this.#currentLiveChannel;
  }

  // プレイヤー

  /**
   * @deprecated
   * {@link IHeliPositionChannelProvider} から逆参照を行うためのメソッドになっている。
   */
  createPlayer(
    playerId: string,
    userCode: string | null,
    channelSessionCode: string | null,
    role: string | null,
    rtcUserCode: number | null
  ): void {
    const player = new CHeliPlayer({
      playerId,
      userCode,
      role,
      rtcUserCode,
      channelSessionCode
    });
    this.#voiceChannelProvider?.initializePlayerVoice(player);
    this.#idPlayerPairs.set(playerId, player);
  }

  releasePlayer(playerId: string): boolean {
    const player = this.getPlayer(playerId);
    if (!player) {
      return false;
    }
    this.#idPlayerPairs.delete(playerId);
    this.#voiceChannelProvider?.onPlayerRemoved(player);
    return true;
  }

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} {@link CHeliSkywayVoiceChannelProvider} 特有のメソッドだが、グローバル関数から参照されている。
   */
  setPlayerStream(stream: RoomStream): void {
    if (!stream.peerId) {
      // TODO: Smcn、Agoraの場合?
      // そもそもこのメソッドは旧Skyway、Skyway2以外でも呼ばれるのか?
      throw new Error('stream.peerId not');
    }

    this.#voiceChannelProvider?.setPlayerVoiceStream(
      this.getPlayer(stream.peerId) ?? stream.peerId,
      stream
    );
  }

  getPlayer(playerId: string): CHeliPlayer | null {
    return this.#idPlayerPairs.get(playerId) ?? null;
  }

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider}、{@link CHeliSkywayVoiceChannelProvider} から逆参照を行うためのメソッド。
   */
  releaseAllPlayer(): void {
    const playerList = this.getPlayerList();
    for (const playerId of playerList.keys()) {
      // TODO: このへん整理したい
      requestSendingReceivedPacketsFromSkyway('leave', playerId, '');
    }
    playerList.clear();
  }

  /** playerId をキーに持つプレイヤーリストを返す。 */
  getPlayerList() {
    return this.#idPlayerPairs;
  }

  /**
   * リモートPlayerのStream受信に自動再生できなかった場合に
   */
  pushPendingRemoteAudioList({
    player,
    audio
  }: {
    player: CHeliPlayer;
    audio: HTMLAudioElement;
  }): void {
    const rtcUserCode = player.getRtcUserCode();
    if (rtcUserCode) {
      this.#pendingRemoteAudioList.set(rtcUserCode, audio); // <-- Mapにset
    }

    if (this.#remoteAudioRetryInProcessFlg === false) {
      this.#remoteAudioRetryInProcessFlg = true;
      // HUDにリモートPlayerの音声再生失敗を通知
      hel_send_notify_message(
        10,
        '【プレーヤー音声再生エラー】\nクリックすると音声再生します。\nプレーヤ音声を自動再生するにはマイクを許可してください',
        600,
        'remote_voice_stream_play_error',
        ''
      );
      console.debug('Playback blocked. Please tap the screen to enable audio.');

      if (this.#remoteAudioRetryInitialized === false) {
        // #retryHandlerメソッドをthisにバインドしてイベントリスナーに登録
        const mainCanvas = getMainCanvasDOMElement();
        if (mainCanvas) {
          mainCanvas.addEventListener('touchend', this.#retryHandler);
          this.#remoteAudioRetryInitialized = true;
        }
      }
    }
  }

  /**
   * touchendイベントハンドラ
   */
  #retryHandler = async (): Promise<void> => {
    console.debug('touchend received on canvas.');
    if (this.#remoteAudioRetryInProcessFlg) {
      // touchendのイベントスタック内で直接リトライを開始
      await this.#retry_play_pennding_stream_audio();
    }
  };

  /**
   * 保留リストの音声再生を試行し、AudioContextをアクティベートする
   */
  async #retry_play_pennding_stream_audio(): Promise<void> {
    const showNoticeFlg = !this.#remoteAudioRetryStartedFlg;
    this.#remoteAudioRetryStartedFlg = true;
    const audioContextMain = getMainAudioContext();
    if (!audioContextMain) {
      console.debug('AudioContext is not available.');
      return;
    }

    // 1. AudioContextのアクティベートを試行
    if (audioContextMain.state !== 'running') {
      await audioContextMain.resume().catch((e) => {
        console.debug('AudioContextの再開に失敗:', e);
      });
    }

    // HUDに再生再開を通知
    if (showNoticeFlg) {
      hel_send_notify_message(
        0,
        '再生保留中のプレーヤ音声を再生開始中…',
        30,
        'remote_voice_stream_play_retry_start',
        ''
      );
    }

    // 2. Mapを反復処理し、再生を試行
    // 成功したキーを格納するリスト
    const successfullyPlayedKeys: number[] = [];

    for (const [rtcUserCode, audio] of this.#pendingRemoteAudioList.entries()) {
      // <-- Mapを反復処理
      try {
        await audio.play();
        console.debug(`リモートストリーム (Code: ${rtcUserCode}) の手動再生に成功。`, audio);
        successfullyPlayedKeys.push(rtcUserCode); // 成功したキーを記録
      } catch (e) {
        if (e instanceof Error) {
          console.debug(`リモート音声 (Code: ${rtcUserCode}) の手動再生に失敗: ${e.name}`, e);
        } else {
          console.debug(`リモート音声 (Code: ${rtcUserCode}) の手動再生に失敗: 不明なエラー`, e);
        }
      }
    }

    // 3. 成功した要素をMapから削除
    for (const key of successfullyPlayedKeys) {
      this.#pendingRemoteAudioList.delete(key);
    }

    // 4. 完了条件のチェック
    if (audioContextMain.state === 'running' && this.#pendingRemoteAudioList.size === 0) {
      // <-- Mapのサイズでチェック
      // AudioContextがアクティブになり、かつ保留リストが空になった場合
      this.#remoteAudioRetryInProcessFlg = false;
      this.#remoteAudioRetryInitialized = false;
      this.#remoteAudioRetryStartedFlg = false;
      // 【TODO: 通知メッセージ送信関数を呼び出す (再生成功)】
      console.debug('Audio playback retry successfully completed and listener removed.');
    } else {
      // AudioContextがsuspendedであるか、または一部のストリームが再生に失敗している場合
      this.#remoteAudioRetryInProcessFlg = true; // 次のタップを受け付ける
      // 【TODO: 通知メッセージ送信関数を呼び出す (再タップ要求)】
      console.debug(`Retry is still needed. ${this.#pendingRemoteAudioList.size} streams pending.`);
    }
  }

  /**
   * 指定したplayerIdのchannelSessionCodeを返す
   * @param playerId `null` の場合は自身のを返す
   * @returns playerIdに対応するプレイヤーが見つからなかった場合は空文字列、channelSessionCodeが取得できなかった場合は `undefined`
   */
  getChannelSessionCodeByPlayerId(playerId: string | null): string | undefined {
    const channelSessionCode = this.#selfPlayerInfo?.channel_session_code;
    if (!playerId) {
      return channelSessionCode;
    }

    if (playerId === this.getPlayerId()) {
      return channelSessionCode;
    }

    for (const [objectId, player] of this.getPlayerList().entries()) {
      if (player.getPlayerId() === playerId) {
        return player.getChannelSessionCode() ?? undefined;
      }
    }
    return '';
  }

  /**
   * 指定したchannelSessionCodeのplayerIdを返す
   * @param channelSessionCode `null` の場合は自身のを返す
   * @returns
   */
  getPlayerIdByChannelSessionCode(channelSessionCode: string | null): string | null {
    const playerId = this.getPlayerId();
    if (!channelSessionCode) {
      return playerId;
    }

    if (channelSessionCode === this.#selfPlayerInfo?.channel_session_code) {
      return playerId;
    }

    for (const player of this.getPlayerList().values()) {
      if (player.getChannelSessionCode() === channelSessionCode) {
        return player.getPlayerId();
      }
    }
    return null;
  }

  /**
   * TODO
   * エンジン対応するまでの暫定実装
   */
  getSelfPlayerRole(): string | null {
    if (!this.#selfPlayerInfo) {
      console.error('this.#selfPlayerInfo is null.');
      return null;
    }
    if (!this.#selfPlayerInfo.user_role) {
      if (hel_net_is_logged_in()) {
        console.error('this.#selfPlayerInfo.user_role is not defined or null.');
      }
      return null;
    }
    return this.#selfPlayerInfo.user_role;
  }

  sendMyPlayerInfoToAllPlayer(): void {
    if (!this.isConnectedPositionChannel()) return;

    this.sendData(
      'join:' +
        getSkywayName() +
        ':' +
        getSkywayAvatarNumberForSend() +
        ':' +
        (getSkywayStartToggle() ? 1 : 0) +
        ':' +
        (getSkywayUseCustomAvatarIcon() ? 1 : 0)
    );

    // アバター情報を送信する
    if (getSkywayAvatarNumberForSend() !== -1) {
      this.sendData('avatar:' + getSkywayAvatarNumberForSend());
    }

    // カスタムアバターアイコンのURLを送信する
    if (getSkywayUseCustomAvatarIcon() && getSkywayCustomAvatarIconURL() !== '') {
      this.sendData('customavataricon:' + getSkywayCustomAvatarIconURL());
    }

    // カスタムユーザーデータを送信する
    hel_skyway_myinfo_bridge_call();
  }

  // プレイヤー：音量

  /**
   * @remarks 一時ブロック時に呼ばれる。
   */
  setPlayerVoiceMute(playreId: string, mute: boolean): void {
    const player = this.getPlayer(playreId);
    if (player === null) return;

    player.mute = mute;
    this.#voiceChannelProvider?.applyPlayerVoiceMute(player);
  }

  setPlayerVoiceAttenuate(playerId: string, volume: number): void {
    const player = this.getPlayer(playerId);
    if (!player) {
      return;
    }
    this.#voiceChannelProvider?.setPlayerVoiceAttenuate(player, volume);
  }

  /** スピーカー音量のうち、他プレイヤーの声の音量を設定する。 */
  setGlobalPlayerVoiceVolume(volume: number): void {
    this.#voiceChannelProvider?.setGlobalPlayerVoiceVolume(volume);
  }

  // 低レベルデータ送信

  sendData(param: string): void {
    this.#positionChannelProvider?.sendData(param);
  }

  // Media入力

  async startScreenShare(
    videoItemName: string,
    videoMaterialName: string,
    useCamera: boolean,
    useAudio: boolean
  ): Promise<void> {
    if (!window.navigator.mediaDevices) {
      console.error('navigator.mediaDevices not found');
      return;
    }

    const localVideoMediaStream = await this.#voiceChannelProvider?.startScreenShareStream(
      useCamera,
      useAudio
    );
    if (!localVideoMediaStream) {
      return;
    }

    // ローカル画面共有の再生
    hel_video_stop();
    helcore_video_play_mediastream(
      videoItemName,
      videoMaterialName,
      localVideoMediaStream,
      true,
      false,
      false,
      null
    );

    this.#setScreenShareCurrentInfo(videoItemName, videoMaterialName);

    // 全ユーザーに画面共有の開始を通知する
    this.sendScreenShareStart();

    // グローバルに hel_onScreenShareStart が定義されていた場合に実行する
    if (typeof globalThis.hel_onScreenShareStart === 'function') {
      globalThis.hel_onScreenShareStart();
    }

    // HeliScriptのコールバックを呼び出す
    hel_setText(ETextParamType_OnStartScreenShare, '');
  }

  async stopScreenShare(): Promise<void> {
    hel_video_stop();

    if (!(await this.#voiceChannelProvider?.cleanupScreenShareStream())) {
      return;
    }

    this.#setScreenShareCurrentInfo('', '');
  }

  playScreenShare(): void {
    this.sendData('screenshare:waitstart');
    this.#voiceChannelProvider?.onPlayScreenShare();
  }

  playScreenShareReserved(): void {
    if (!this.#skyway_reseved_remoteVideoStream) {
      console.error('this.m_skyway_reseved_remoteVideoStream is null');
      return;
    }

    hel_skyway_play_remote_video_stream(
      this.#skyway_reseved_remoteVideoStream,
      this.#skyway_reseved_videoItemName,
      this.#skyway_reseved_videoMaterialName
    );
  }

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} から逆参照を行うためのメソッド。
   */
  reserveRemotoVideoStream(
    remoteVideoStream: MediaStream,
    videoItemName: string,
    videoMaterialName: string
  ): void {
    this.#skyway_reseved_remoteVideoStream = remoteVideoStream;
    this.#skyway_reseved_videoItemName = videoItemName;
    this.#skyway_reseved_videoMaterialName = videoMaterialName;
  }

  async initLocalMicInput(deviceId: string | null): Promise<boolean> {
    console.debug('initLocalMicInput deviceId', deviceId);
    const promises = [this.#initLocalVoiceMicInput(deviceId)];
    if (this.#liveChannelProvider && !this.#voiceChannelProvider?.supportsSpeakerMode()) {
      if (
        this.#currentLiveChannel &&
        this.#liveChannelProvider.isLiveChannelPresenter(this.#currentLiveChannel)
      ) {
        promises.push(this.#initLocalLiveMicInput(deviceId));
      }
    }
    const result = (await Promise.all(promises)).every((result) => result);
    if (result) {
      await this.setCurrentMicDevice(deviceId);
    }
    return result;
  }
  async #initLocalVoiceMicInput(deviceId: string | null): Promise<boolean> {
    try {
      if (!this.#voiceChannelProvider?.initLocalMicInput(deviceId)) {
        return false;
      }
      this.micReady = true;
      return true;
    } catch (error) {
      // TODO
      console.error(error);
      return false;
    }
  }
  async #initLocalLiveMicInput(deviceId: string | null): Promise<boolean> {
    console.debug('initLocalLiveMicInput deviceId', deviceId);
    if (!this.#liveChannelProvider) {
      throw new Error('this.#liveChannelProvider is null.');
    }
    try {
      await this.#liveChannelProvider.initLocalMicInput(deviceId);
      this.liveMicReady = true;
      return true;
    } catch (error) {
      // TODO
      console.error(error);
      return false;
    }
  }

  /** ローカルのマイク入力の有効無効 */
  async setMicEnabled(enabled: boolean): Promise<void> {
    setMicEnabled(enabled);
    await this.#voiceChannelProvider?.setMicEnabled(enabled);
  }
  /** ローカルのマイク入力の有効無効（ライブ配信用） */
  async setLiveMicEnabled(enabled: boolean): Promise<void> {
    if (!this.#liveChannelProvider) {
      throw new Error('Method not implemented.');
    }

    if (this.#voiceChannelProvider?.supportsSpeakerMode()) {
      // ライブ配信のマイク音声は、音声チャンネルがスピーカーモードに対応している場合、
      // ライブチャンネルではなく音声チャンネルを利用するため、通常の処理へ合流
      await this.setMicEnabled(enabled);
      return;
    }

    setLiveMicEnabled(enabled);
    await this.#liveChannelProvider.setLiveMicEnabled(enabled);
  }

  getMicEnabled(): boolean {
    return this.#voiceChannelProvider?.getMicEnabled() ?? false;
  }

  getLiveMicEnabled(): boolean {
    if (this.#voiceChannelProvider?.supportsSpeakerMode()) {
      // ライブ配信のマイク音声は、音声チャンネルがスピーカーモードに対応している場合、
      // ライブチャンネルではなく音声チャンネルを利用するため、通常の処理へ合流
      return this.getMicEnabled();
    }

    return this.#liveChannelProvider?.getLiveMicEnabled() ?? false;
  }

  async setCurrentMicDevice(deviceId: string | null): Promise<void> {
    const devices = await hel_net_get_mic_device_list();
    const currentDevice =
      deviceId !== null ? devices?.find((device) => device.deviceId === deviceId) : null;
    const device = currentDevice ?? devices?.[0];
    this.#currentMic = device ? {label: device.label, deviceId: device.deviceId} : null;
  }

  // Smcnのdisconnect時にMediaStreamがendedになるので、その回避用
  async checkTrackState(deviceId: string | null): Promise<void> {
    let localStream = getLocalStream() ?? null;
    if (localStream) {
      // localStreamがstop済みの場合があるため、その場合は新たに作り直す
      const track = localStream.getAudioTracks()[0];
      if (track?.readyState === 'ended') {
        // NOTE: マイクデバイスが存在しない場合、trackがundefinedとなり、ここには到達しない
        localStream = await this.#createMediaStream(deviceId);
        if (!localStream) {
          throw new Error('CHeliNetwork.#createMediaStream() returns null');
        }
        setLocalStream(localStream);
      }
    }
  }

  /**
   * localMediaStreamの初期化。
   * @param deviceId
   * @returns 次の場合に `null` が返る。
   * - マイク無許可時
   * - `deviceId` に string 指定した場合に、デバイスが存在しないとき
   * - `deviceId` に `null` を指定した場合に、マイクデバイスが一つも存在しないとき
   */
  async #createMediaStream(deviceId: string | null): Promise<MediaStream | null> {
    // localMediaStreamの初期化
    let localMediaStream: MediaStream;
    try {
      localMediaStream = await window.navigator.mediaDevices.getUserMedia({
        audio: {deviceId: deviceId ? {exact: deviceId} : undefined},
        video: false
      });
    } catch (exception) {
      console.debug('CHeliNetwork.#createMediaStream', exception);
      return null;
    }
    const track = localMediaStream.getAudioTracks()[0];
    if (!track) {
      throw new Error(
        '(await navigator.mediaDevices.getUserMedia()).getAudioTracks() returns an empty array.'
      );
    }
    track.enabled = false;
    return localMediaStream;
  }

  async initMicDevice(): Promise<IHeliMicResponse> {
    // マイクデバイスの取得
    const device = await this.getMicDevice();
    const mediaStream = await this.#createMediaStream(device?.deviceId ?? null);
    if (!mediaStream) {
      return this.#createMicDeviceResponse(
        buildErrorCode('Platform', 'TypeScript', 'MediaStream', 'Audio', 'Exception')
      );
    }
    setLocalStream(mediaStream);
    return this.#createMicDeviceResponse();
  }

  async #initLiveMediaStream(): Promise<boolean> {
    // マイクデバイスの取得
    const device = await this.getMicDevice();
    const mediaStream = await this.#createMediaStream(device?.deviceId ?? null);
    if (!mediaStream) {
      return false;
    }
    setLiveLocalStream(mediaStream);
    return true;
  }

  getCurrentMicDevice(): IHeliMic | null {
    return this.#currentMic;
  }

  async changeMicDevice(deviceId: string): Promise<IHeliMicResponse> {
    await this.setCurrentMicDevice(deviceId);
    if (!getLocalStream()) {
      await this.initMicDevice();
    }
    try {
      if (this.#liveChannelProvider && !this.#voiceChannelProvider?.supportsSpeakerMode()) {
        if (
          this.#currentLiveChannel &&
          this.#liveChannelProvider.isLiveChannelPresenter(this.#currentLiveChannel)
        ) {
          if (await this.#liveChannelProvider.changeMicDevice(deviceId)) {
            // もしボイスチャンネルに入室している場合は、入室しているボイスチャンネルも更新する
            if (!this.#voiceChannelProvider?.isConnected()) {
              return this.#createMicDeviceResponse();
            }
          }
        }
      }

      if (
        this.#voiceChannelProvider &&
        (await this.#voiceChannelProvider.changeMicDevice(deviceId))
      ) {
        return this.#createMicDeviceResponse();
      }
      return this.#createMicDeviceResponse(
        buildErrorCode('Platform', 'TypeScript', 'MediaStream', 'Audio', 'MicDeviceChange')
      );
    } catch (error) {
      console.error('CHeliNetwork.changeMicDevice', error);
      return this.#createMicDeviceResponse(
        buildErrorCode('Platform', 'TypeScript', 'MediaStream', 'Audio', 'MicDeviceException')
      );
    }
  }

  #createMicDeviceResponse(errorCode?: string): IHeliMicResponse {
    if (!errorCode) {
      return {status: 'ok', device_info: this.#currentMic};
    }
    return {status: 'ng', device_info: this.#currentMic, errorCode: errorCode};
  }

  changeSpeakerMode(speakerMode: boolean): boolean {
    return !this.#voiceChannelProvider
      ? false
      : this.#voiceChannelProvider.setSpeakerMode(speakerMode);
  }

  async apiChangeItemPropertySender(
    refId: string,
    key: string,
    value: string
  ): Promise<object | null> {
    if (!this.#currentPositionChannel) {
      return null;
    }
    const currentPositionChannelDetail = this.#currentPositionChannel.getPositionChannelDetail();
    if (!currentPositionChannelDetail) {
      throw new Error('#currentPositionChannel.getPositionChannelDetail() returns null');
    }
    // APIにメッセージを送信
    return await globalThis.heliport.v3.api.ingameStateManagerApi.changeItemProperty(
      currentPositionChannelDetail.spatium_code,
      currentPositionChannelDetail.world_code,
      currentPositionChannelDetail.channel_id,
      refId,
      {[key]: value}
    );
  }

  async apiChangeItemPropertySenderMulti(
    refId: string,
    data: Record<string, string>
  ): Promise<object | null> {
    if (!this.#currentPositionChannel) {
      return null;
    }
    const currentPositionChannelDetail = this.#currentPositionChannel.getPositionChannelDetail();
    if (!currentPositionChannelDetail) {
      throw new Error('#currentPositionChannel.getPositionChannelDetail() returns null');
    }
    // APIにメッセージを送信
    return await globalThis.heliport.v3.api.ingameStateManagerApi.changeItemProperty(
      currentPositionChannelDetail.spatium_code,
      currentPositionChannelDetail.world_code,
      currentPositionChannelDetail.channel_id,
      refId,
      data
    );
  }

  async apiCommandItemPropertySender(commands: Command[]): Promise<object | null> {
    if (!this.#currentPositionChannel) {
      return null;
    }
    const currentPositionChannelDetail = this.#currentPositionChannel.getPositionChannelDetail();
    if (!currentPositionChannelDetail) {
      throw new Error('#currentPositionChannel.getPositionChannelDetail() returns null');
    }
    // APIにメッセージを送信
    return await globalThis.heliport.v3.api.ingameStateManagerApi.commandItemProperty(
      currentPositionChannelDetail.spatium_code,
      currentPositionChannelDetail.world_code,
      currentPositionChannelDetail.channel_id,
      commands
    );
  }

  async apiCommandWorldItemPropertySender(commands: JsonObject[]): Promise<object | null> {
    if (!this.#currentPositionChannel) {
      return null;
    }
    const currentPositionChannelDetail = this.#currentPositionChannel.getPositionChannelDetail();
    if (!currentPositionChannelDetail) {
      throw new Error('#currentPositionChannel.getPositionChannelDetail() returns null');
    }
    // APIにメッセージを送信
    return await globalThis.heliport.v3.api.ingameStateManagerApi.commandWorldItemProperty({
      spatiumCode: currentPositionChannelDetail.spatium_code,
      worldCode: currentPositionChannelDetail.world_code,
      command: commands
    });
  }

  async requestRoomItemSyncOwnership(objectIds: Array<string>): Promise<object | null> {
    if (!this.#currentPositionChannel) {
      return null;
    }
    const currentPositionChannelDetail = this.#currentPositionChannel.getPositionChannelDetail();
    if (!currentPositionChannelDetail) {
      throw new Error('m_current_position_channel.positionChannel is null');
    }
    const spatiumCode = currentPositionChannelDetail.spatium_code;
    const worldCode = currentPositionChannelDetail.world_code;
    const channelId = currentPositionChannelDetail.channel_id;
    const res = (await globalThis.heliport.v3.api.ingameStateManagerApi.requestObjectOwnership({
      spatiumCode,
      worldCode,
      channelId,
      objectIds
    })) as Return<RequestObjectOwnershipData>;
    if (res.status === 'ok') {
      res.data.forEach((result) => {
        if (!result.error) {
          this.#assignOwnedObjectId(result.objectId);
        }
      });
    }
    return res;
  }
  async releaseRoomItemSyncOwnership(objectIds: Array<string>): Promise<object | null> {
    if (!this.#currentPositionChannel) {
      return null;
    }
    const currentPositionChannelDetail = this.#currentPositionChannel.getPositionChannelDetail();
    if (!currentPositionChannelDetail) {
      throw new Error('m_current_position_channel.positionChannel is null');
    }
    const spatiumCode = currentPositionChannelDetail.spatium_code;
    const worldCode = currentPositionChannelDetail.world_code;
    const channelId = currentPositionChannelDetail.channel_id;
    objectIds.forEach((objectId) => {
      this.#unassignOwnedObjectId(objectId);
    });
    return await globalThis.heliport.v3.api.ingameStateManagerApi.releaseObjectOwnership({
      spatiumCode,
      worldCode,
      channelId,
      objectIds
    });
  }
  async #refreshItemSyncOwnershipHeartbeat(): Promise<object | null> {
    if (!this.#currentPositionChannel) {
      return null;
    }
    const currentPositionChannelDetail = this.#currentPositionChannel.getPositionChannelDetail();
    if (!currentPositionChannelDetail) {
      throw new Error('m_current_position_channel.positionChannel is null');
    }
    const spatiumCode = currentPositionChannelDetail.spatium_code;
    const worldCode = currentPositionChannelDetail.world_code;
    const channelId = currentPositionChannelDetail.channel_id;
    const objectIds = [...this.#ownedObjectIdSetList];
    const result = await globalThis.heliport.v3.api.ingameStateManagerApi.refreshObjectOwnership({
      spatiumCode,
      worldCode,
      channelId,
      objectIds
    });
    return result;
  }
  #assignOwnedObjectId(objectId: string): void {
    this.#ownedObjectIdSetList.add(objectId);
  }
  #unassignOwnedObjectId(objectId: string): void {
    this.#ownedObjectIdSetList.delete(objectId);
  }
  #startOwnershipHeartbeatInterval() {
    window.setInterval(() => {
      if (this.#ownedObjectIdSetList.size > 0) {
        this.#refreshItemSyncOwnershipHeartbeat();
      }
    }, 5000);
  }

  async joinLiveChannelAsHost(
    liveChannel: CHeliLiveChannel,
    pHeliMic: IHeliMic | null
  ): Promise<void> {
    if (!this.#liveChannelProvider) {
      throw new Error('Method not implemented.');
    }

    console.debug('joinliveChannel start:', pHeliMic);

    const channelId = liveChannel.getChannelId();
    if (!channelId) {
      await this.#handleJoinLiveChannelError(liveChannel, 'channelId is null');
      return;
    }
    try {
      const resEntryVoCh =
        await globalThis.heliport.v3.api.channelSessionApi.enterLiveChannel(channelId);
      if (resEntryVoCh.status !== 'ok') {
        // 入室エラー
        liveChannel.setError({
          code: 'errorCode' in resEntryVoCh ? resEntryVoCh.errorCode : resEntryVoCh.code,
          data: resEntryVoCh.data
        });
        return;
      }
      if (!this.#voiceChannelProvider?.supportsSpeakerMode() && !this.liveMicReady) {
        if (!(await this.#initLiveMediaStream())) {
          await this.#handleJoinLiveChannelError(
            liveChannel,
            'CHeliNetwork.#initLiveMediaStream() returns false.'
          );
          return;
        }
        // ライブ配信のマイク音声は、音声チャンネルがスピーカーモードに対応している場合、
        // HostとしてJoinしても、ライブチャンネルではなく音声チャンネルを利用するため、マイク関係の処理はオミット
        await this.#initLocalLiveMicInput(pHeliMic?.deviceId ?? null);
        this.#liveChannelProvider.initializeLiveVoice();
      }

      // crp1.2になったらjoinでuserが取れたらuserCodeで
      // const decodeJwt = await this.getDecodedChannelSession()
      // const userCode = decodeJwt.user_code
      const channelSessionJwt = (
        await globalThis.heliport.v3.api.channelSessionApi.getChannelSessionJwt()
      ).data;
      if (!channelSessionJwt) {
        await this.#handleJoinLiveChannelError(
          liveChannel,
          'channelSessionApi.getChannelSessionJwt() returns null'
        );
        return;
      }
      console.debug('VoCh join genericRtcToken channelSessionJwt', parseJwt(channelSessionJwt));
      const type = this.#liveChannelProvider.getGenericRtcTokenType();
      const mode = 'live';
      const res = await globalThis.heliport.v3.api.genericRtcToken.getToken(
        channelSessionJwt,
        type,
        mode,
        false,
        undefined
      );
      if (res.status !== 'ok') {
        await this.#handleJoinLiveChannelError(liveChannel, res);
        return;
      }
      const token = res.data;
      console.debug('joinliveChannel genericRtcToken jsonToken', token);

      this.#currentLiveChannel = liveChannel;

      // currentLiveChannelがnullの場合は何もしない
      // 入室中に退室が走るケースがあるため、それのケア用
      if (!this.#currentLiveChannel) {
        onNetLiveDisconnected();
        return;
      }

      await this.#liveChannelProvider.joinLiveChannelAsHostWithToken(token);

      if (!this.#voiceChannelProvider?.supportsSpeakerMode()) {
        // ライブ配信のマイク音声は、音声チャンネルがスピーカーモードに対応している場合、
        // HostとしてJoinしても、ライブチャンネルではなく音声チャンネルを利用するため、マイク関係の処理はオミット
        await this.checkMicPermissionState();
        // TODO: ここでコールバックを呼び出すよりも、agoraClientのイベントから呼び出したい
        // onNetLiveConnected(agoraUserId);
      }

      console.debug('joinliveChannel done');
    } catch (error) {
      await this.#handleJoinLiveChannelError(liveChannel, error);
    }
  }

  async joinLiveChannelAsAudience(liveChannel: CHeliLiveChannel): Promise<void> {
    if (!this.#liveChannelProvider) {
      throw new Error('Method not implemented.');
    }

    try {
      const liveChannelId = liveChannel.getChannelId();
      if (!liveChannelId) {
        await this.#handleJoinLiveChannelError(liveChannel, 'liveChannel.getChannelId() is null');
        return;
      }
      const res = await globalThis.heliport.v3.api.channelSessionApi.fetchLiveChannelAudienceJwt(
        liveChannelId,
        null
      );
      if (res.status !== 'ok') {
        // 入室エラー
        if (this.#currentLiveChannel?.getChannelId() === liveChannelId) {
          await this.leaveLiveChannel(this.#currentLiveChannel);
        }
        this.setError({code: 'errorCode' in res ? res.errorCode : res.code, data: res.data});
        return;
      }

      const type = this.#liveChannelProvider.getGenericRtcTokenType();

      const resGetToken = await globalThis.heliport.v3.api.genericRtcToken.getToken(
        res.data.jwt,
        type,
        'live',
        false,
        undefined
      );
      console.debug(resGetToken);
      if (resGetToken.status !== 'ok') {
        await this.#handleJoinLiveChannelError(liveChannel, resGetToken);
        return;
      }
      const token = resGetToken.data;

      this.#currentLiveChannel = liveChannel;

      // currentLiveChannelがnullの場合は何もしない
      // 入室中に退室が走るケースがあるため、それのケア用
      if (!this.#currentLiveChannel) {
        onNetLiveDisconnected();
        return;
      }

      console.debug('joinLiveChannel audience start');

      await this.#liveChannelProvider.joinLiveChannelAsAudienceWithToken(token);

      // TODO: ここでコールバックを呼び出すよりも、agoraClientのイベントから呼び出したい
      // this.m_parent.m_IHeliNetworkCallback?.OnNetLiveConnected(agoraUserId);

      console.debug('joinLiveChannel audience done');
    } catch (error) {
      await this.#handleJoinLiveChannelError(liveChannel, error);
    }
  }

  async leaveLiveChannel(liveChannel: CHeliLiveChannel): Promise<void> {
    if (this.#liveChannelProvider?.isLiveChannelPresenter(liveChannel)) {
      try {
        const resLeaveLiveCh =
          await globalThis.heliport.v3.api.channelSessionApi.leaveLiveChannel();
        if (resLeaveLiveCh.status !== 'ok') {
          this.setError({
            code: 'errorCode' in resLeaveLiveCh ? resLeaveLiveCh.errorCode : resLeaveLiveCh.code,
            data: resLeaveLiveCh.data
          });
          return;
        }
      } catch (error) {
        liveChannel.setError({code: '400', data: error});
        return;
      }
    }
    // もしライブ配信再生途中で、leaveした場合、配信を停止する
    if (this.isPlayingLiveVideo()) {
      this.stopLiveVideo();
    }
    this.#currentLiveChannel = null;
    this.#clearLiveVideoSrc();
    await this.#liveChannelProvider?.leaveLiveChannel();
    onNetLiveVideoEnded();
  }

  #stopRemoteVideoLiveStream() {
    if (this.isPlayingLiveVideo()) {
      this.#clearLiveVideoSrc();
      this.stopLiveVideo();
    }
    onNetLiveVideoEnded();
  }

  /** ライブチャンネル動画 MediaStream受け取り */
  #prepareLiveVideoSrc(mediaStream: MediaStream) {
    this.#setLiveVideoSrc(mediaStream);
    // HeliScript側にストリーム受信通知
    onNetLiveVideoStarted();
    this.#checkAndPlayVideoStream();
  }

  findPlayerByRtcUserCode(rtcUserCode: number): CHeliPlayer | null {
    return (
      Array.from(this.#idPlayerPairs.values()).find(
        (player) => player.getRtcUserCode() === rtcUserCode
      ) ?? null
    );
  }

  /** ライブチャンネル動画 動画再生パネルの設定 */
  prepareLiveVideoItem(itemName: string, materialName: string): void {
    if (!this.#liveChannelProvider) {
      return;
    }
    this.#liveVideoItem = {itemName, materialName};
    this.#checkAndPlayVideoStream();
  }

  /** ライブチャンネル動画 再生待機 */
  standbyPlayLiveVideo(): void {
    if (!this.#liveChannelProvider) {
      return;
    }
    this.#stanbyPlayVideoStreamFlg = true;
    this.#checkAndPlayVideoStream();
  }

  /** ライブチャンネル動画の再生 ※ prepare済 */
  playLiveVideo(): boolean {
    const liveChannelProvider = this.#liveChannelProvider;
    if (!liveChannelProvider) {
      return false;
    }
    if (!this.isLiveVideoPlayReady()) {
      return false;
    }
    if (!this.#liveVideoItem) {
      return false;
    }
    if (!this.#liveVideoSrc) {
      return false;
    }
    hel_video_stop();
    helcore_video_play_mediastream(
      this.#liveVideoItem.itemName,
      this.#liveVideoItem.materialName,
      this.#liveVideoSrc,
      true,
      false,
      false,
      null
    );
    onNetLiveVideoPlay();
    liveChannelProvider.unmuteLiveStreamAudio();
    hel_video_open_genericwindow();
    return true;
  }

  /** ライブチャンネル動画の停止 */
  stopLiveVideo(): void {
    hel_video_stop();
    onNetLiveVideoStop();
    hel_video_close_genericwindow();
  }

  /** ライブチャンネル動画の再生中判定 */
  isPlayingLiveVideo(): boolean {
    const video = getVideo();
    return video &&
      !video.paused &&
      video.srcObject &&
      this.#liveVideoSrc &&
      'id' in video.srcObject &&
      video.srcObject.id === this.#liveVideoSrc.id
      ? true
      : false;
  }

  #checkAndPlayVideoStream(): string {
    if (this.#liveVideoSrc === null) {
      const result = 'FAIL_NO_STREAM';
      console.error('LiveVideo checkAndPlayVideoStream', result);
      return result;
    }
    if (this.#liveVideoItem === null) {
      const result = 'FAIL_NO_PLAY_ITEM';
      console.error('LiveVideo checkAndPlayVideoStream', result);
      return result;
    }
    if (!this.isLiveVideoPlayReady()) {
      const result = 'FAIL_NO_VIDEO_TRACK';
      console.error('LiveVideo checkAndPlayVideoStream', result);
      return result;
    }

    if (!this.#stanbyPlayVideoStreamFlg) {
      const result = 'FAIL_NO_PLAY_STANDBY';
      console.error('LiveVideo checkAndPlayVideoStream', result);
      return result;
    }

    // このまま自動再生するか
    if (this.#autoPlayLiveVideoStreamFlg) {
      const result = 'PLAY_START';
      this.playLiveVideo();
      return result;
    }

    const result = 'PLAYABLE';
    console.debug('LiveVideo checkAndPlayVideoStream', result, this.isPlayingLiveVideo());
    // HeliScript側に再生準備完了通知（新UI）
    if (!this.isPlayingLiveVideo()) {
      onNetLiveConfirmPlayVideo();
    }
    return result;
  }

  /**
   * ライブチャンネル動画の再生可能か
   * @remarks
   * ライブ動画再生に必要なデータが揃っているか
   */
  isLiveVideoPlayReady(): boolean {
    if (
      !this.#liveChannelProvider ||
      this.#liveVideoItem === null ||
      !this.#liveVideoItem.itemName ||
      !this.#liveVideoItem.materialName
    ) {
      return false;
    }
    if (this.#liveVideoSrc === null) {
      return false;
    }
    if (
      this.#liveVideoSrc &&
      this.#liveVideoSrc.getVideoTracks &&
      this.#liveVideoSrc.getVideoTracks().length > 0
    ) {
      return true;
    }
    console.error('no video tracks');
    return false;
  }

  /** ライブチャンネル動画の自動再生設定 */
  setLiveVideoAutoPlay(autoPlay: boolean): void {
    if (!this.#liveChannelProvider) {
      return;
    }
    this.#autoPlayLiveVideoStreamFlg = autoPlay;
  }

  isConnectedLiveChannel(liveChannel: CHeliLiveChannel): boolean {
    return this.#liveChannelProvider?.isConnectedLiveChannel(liveChannel) ?? false;
  }

  #clearLiveVideoSrc(): void {
    this.#setLiveVideoSrc(null);
  }
  #setLiveVideoSrc(mediaStream: MediaStream | null): void {
    this.#liveVideoSrc = mediaStream;
  }

  async #handleJoinLiveChannelError(liveChannel: CHeliLiveChannel, error: unknown): Promise<void> {
    console.error(error, this.#currentLiveChannel);

    // 入室中に退室が走るケースがあるため、それのケア用
    if (!this.#currentLiveChannel) {
      onNetLiveDisconnected();
    }

    if (this.#currentLiveChannel?.getChannelId() === liveChannel.getChannelId()) {
      await this.leaveLiveChannel(liveChannel);
    }
    liveChannel.setError({code: '400', data: error});
  }

  #setScreenShareCurrentInfo(videoItemName: string, videoMaterialName: string): void {
    this.#screenShare_CurrentVideoItemName = videoItemName;
    this.#screenShare_CurrentVideoMaterialName = videoMaterialName;
  }

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} {@link CHeliSkywayVoiceChannelProvider} {@link CHeliAgoraVoiceChannelProvider} から逆参照を行うためのメソッド。
   */
  sendScreenShareStart(): void {
    if (this.#screenShare_CurrentVideoItemName === '') return;
    if (this.#screenShare_CurrentVideoMaterialName === '') return;

    this.sendData(
      `screenshare:start:${this.#screenShare_CurrentVideoItemName}:${this.#screenShare_CurrentVideoMaterialName}`
    );
  }

  /** 他プレーヤのアバター表示切替コールバック */
  setPlayerFullAvatarState(playerId: string, isFullAvatar: boolean): void {
    const player = this.getPlayer(playerId);
    if (!player) {
      throw new Error(`Player with ID “${playerId}” not found. Unable to set full avatar state.`);
    }
    this.#positionChannelProvider?.setPlayerFullAvatarState(player, isFullAvatar);
  }
  /** 他プレーヤの他プレーヤのモーショントラッキング使用状況切替コールバック */
  setPlayerMotionTrackingState(playerId: string, isMotionTracking: boolean): void {
    const player = this.getPlayer(playerId);
    if (!player) {
      throw new Error(
        `Player with ID “${playerId}” not found. Unable to set motion tracking state.`
      );
    }
    this.#positionChannelProvider?.setPlayerMotionTrackingState(player, isMotionTracking);
  }
  sendHighFrequentSyncSubscribe(playerId: string, subscribe: boolean): void {
    const player = this.getPlayer(playerId);
    if (!player) {
      throw new Error(
        `Player with ID “${playerId}” not found. Unable to set motion tracking state.`
      );
    }
    this.#positionChannelProvider?.sendHighFrequentSyncSubscribe(player, subscribe);
  }

  /**
   * @deprecated
   * {@link CHeliOldSkywayUnifiedPositionVoiceChannelProvider} 特有のメソッドだが、グローバル関数から参照されている。
   */
  connectRoomAfterPeerOpen(): void {
    this.#voiceChannelProvider?.connectRoomAfterPeerOpen();
  }
}
